import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import Navbar from "../../components/LandPage/Navbar"
import { GetRequest } from "../../services/ApiClientService"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { SEO } from "../../seo/Seo"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { LoadingComponet } from "../../PublicMenu/Components/LoadingComponet"
import { organizationSchema } from "../../seo/schema"
import {
  PublicAnimatedBackground,
  publicAnimatedBackgroundShellClassName,
} from "../../components/PublicAnimatedBackground/PublicAnimatedBackground"

interface PublicStoreCard {
  slug: string
  companyName: string
  companyMessage?: string
  iconUrl?: string
}

const fallbackStoreIcon = "/vite.svg"

export default function StoreSearchPage() {
  const [stores, setStores] = useState<PublicStoreCard[]>([])

  const [loading, setLoading] = useState(true)

  const [search, setSearch] = useState("")

  const navigate = useNavigate()

  const { t } = useTranslation("frontend")

  useEffect(() => {
    const loadStores = async () => {
      setLoading(true)

      try {
        const response = await GetRequest<PublicStoreCard[]>(API_ROUTES.COMPANY.GET_ACTIVE_PUBLIC_STORES)

        if (response.success) {
          setStores(response.data ?? [])
        }
      } catch {
        setStores([])
      } finally {
        setLoading(false)
      }
    }

    loadStores()
  }, [])

  const filteredStores = useMemo(() => {
    const searchText = search.trim().toLowerCase()

    if (!searchText) {
      return stores
    }

    return stores.filter((store) => {
      const companyName = store.companyName?.toLowerCase() ?? ""

      const companyMessage = store.companyMessage?.toLowerCase() ?? ""

      return companyName.includes(searchText) || companyMessage.includes(searchText)
    })
  }, [stores, search])

  return (
    <div
      className={`${publicAnimatedBackgroundShellClassName} min-h-dvh bg-white flex flex-col justify-between bg-cover bg-center relative z-0 overflow-x-hidden`}
    >
      <PublicAnimatedBackground />

      <SEO
        title={t("store_search.seo_title")}
        description={t("store_search.seo_description")}
        path="/lojas"
        structuredData={organizationSchema}
      />

      <div>
        <Navbar showReturn={true} showFunctions={false} />

        <section className="pt-28 pb-2 bg-gradient-to-b from-orange-50 via-white to-white px-5">
          <div className="max-w-[1280px] mx-auto">
            <Animate type={AnimationTypes.slideRightIn} duration="1s">
              <span className="inline-flex items-center rounded-md bg-red-100 text-red-600 text-xs font-extrabold px-3 py-1 uppercase tracking-wide border border-orange-200">
                {t("store_search.available_stores")}
              </span>
            </Animate>

            <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
              <h1 className="text-3xl md:text-5xl font-black text-slate-900 mt-4">{t("store_search.title")}</h1>
            </Animate>

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
              <p className="w-full text-slate-600 text-sm md:text-base mt-2">{t("store_search.subtitle")}</p>
            </Animate>

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.4s">
              <div className="mt-5">
                <div className="flex items-center gap-2 border-2 border-orange-200 rounded-lg px-3 py-2 bg-white shadow-sm">
                  <span className="text-lg">🔎</span>

                  <input
                    type="text"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("store_search.search_placeholder")}
                    className="w-full outline-none text-sm md:text-base text-slate-700"
                    data-interaction-description-key="interactionDescriptions:pages.restaurantSearchPage.restaurantSearchPage.input.searchPlaceholder"
                  />
                </div>
              </div>
            </Animate>
          </div>
        </section>

        <section className="px-5 pb-12">
          <div className="max-w-[1280px] mx-auto">
            {loading && <LoadingComponet />}

            {!loading && filteredStores.length === 0 && (
              <div className="border-2 border-orange-200 rounded-lg p-5 bg-orange-50">
                <p className="text-slate-700 font-semibold">{t("store_search.empty")}</p>
              </div>
            )}

            {!loading && filteredStores.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredStores.map((store, index) => (
                  <Animate key={index} type={AnimationTypes.fadeInTraslate} duration="1s">
                    <article
                      key={store.slug}
                      className="border-2 border-orange-200 rounded-lg p-2 bg-white shadow-sm flex flex-col h-full"
                    >
                      <div className="flex items-start gap-3">
                        <img
                          src={store.iconUrl.toClientRequest() || fallbackStoreIcon}
                          alt={t("store_search.store_logo_alt", { name: store.companyName })}
                          className="w-16 h-16 rounded-md border border-orange-200 object-cover bg-white"
                          loading="lazy"
                          decoding="async"
                        />

                        <div>
                          <h2 className="text-lg font-black text-slate-900 leading-tight">{store.companyName}</h2>
                          <p className="text-xs text-orange-700 font-bold mt-1">/{store.slug}</p>
                        </div>
                      </div>

                      {store.companyMessage && (
                        <p className="text-sm text-slate-600 mt-3 line-clamp-3">{store.companyMessage}</p>
                      )}

                      {!store.companyMessage && (
                        <p className="text-sm text-slate-500 mt-3 italic">{t("store_search.no_public_description")}</p>
                      )}

                      <button
                        onClick={() => navigate(`/${store.slug}`)}
                        className="mt-4 w-full cursor-pointer bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-2 rounded-md font-bold transition-all duration-300 hover:scale-[1.01]"
                        data-interaction-description-key="interactionDescriptions:pages.restaurantSearchPage.restaurantSearchPage.button.navigate"
                      >
                        {t("store_search.visit_store")}
                      </button>
                    </article>
                  </Animate>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      {/*<Footer />*/}
    </div>
  )
}
